import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../../App.css";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { useState, useEffect, useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { config } from "../../config";
import { useNavigate } from "react-router-dom";
import { useLoadScript } from "@react-google-maps/api";
import { addDays } from "date-fns";
import debounce from "lodash/debounce";

// Define libraries array outside component
const libraries = ["places"];
const MINIMUM_SEARCH_LENGTH = 3;

function SearchForm({
  initialLocation = "",
  initialDateRange = [new Date(), addDays(new Date(), 1)],
  initialGuests = { adults: 1, children: 0 },
  initialRooms = 1,
}) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    location: initialLocation,
    coordinates: null, // Will store {latitude, longitude}
    dateRange: initialDateRange,
    guests: {
      adults: initialGuests.adults,
      children: initialGuests.children,
      childrenAges: initialGuests.childrenAges || [],
    },
    rooms: initialRooms,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState([]);

  // Update useLoadScript to use static libraries array
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });

  // Debounced geocoding function
  // eslint-disable-next-line
  const debouncedGeocode = useCallback(
    debounce(async (value) => {
      if (!value || value.length < MINIMUM_SEARCH_LENGTH || !isLoaded) return;

      try {
        const geocoder = new window.google.maps.Geocoder();
        const response = await geocoder.geocode({ address: value });

        if (response.results?.length > 0) {
          const { lat, lng } = response.results[0].geometry.location;
          setFormData((prev) => ({
            ...prev,
            coordinates: { latitude: lat(), longitude: lng() },
          }));
          setLocationSuggestions(
            response.results.map((result) => ({
              description: result.formatted_address,
              coordinates: {
                latitude: result.geometry.location.lat(),
                longitude: result.geometry.location.lng(),
              },
            }))
          );
        }
      } catch (error) {
        // Only log errors for searches that meet minimum length
        if (value.length >= MINIMUM_SEARCH_LENGTH) {
          console.error("Error fetching location suggestions:", error);
        }
      }
    }, 500), // 500ms delay
    [isLoaded]
  );

  const handleDateChange = (dates) => {
    const [checkIn, checkOut] = dates;
    setFormData({ ...formData, dateRange: [checkIn, checkOut] });
  };

  const handleLocationChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, location: value }));

    // Only trigger geocoding if we have enough characters
    if (value.length >= MINIMUM_SEARCH_LENGTH) {
      debouncedGeocode(value);
    } else {
      setLocationSuggestions([]); // Clear suggestions if input is too short
    }
  };

  // Clear debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedGeocode.cancel();
    };
  }, [debouncedGeocode]);

  const handleSearch = () => {
    if (!formData.coordinates) {
      alert("Please select a valid location from the suggestions");
      return;
    }

    const [checkIn, checkOut] = formData.dateRange;
    const formatDate = (date) => date.toISOString().split("T")[0];

    const params = new URLSearchParams({
      latitude: formData.coordinates.latitude,
      longitude: formData.coordinates.longitude,
      locationName: formData.location,
      radius: 5,
      checkIn: formatDate(checkIn),
      checkOut: formatDate(checkOut),
      rooms: formData.rooms,
      adults: formData.guests.adults,
      children: formData.guests.children,
      childrenAges: formData.guests.childrenAges.join(","),
      currency: "GBP",
    });

    navigate(`/hotels/search?${params.toString()}`);
  };

  const updateGuests = (type, action) => {
    setFormData((prev) => {
      const newGuests = { ...prev.guests };
      newGuests[type] =
        action === "increment"
          ? newGuests[type] + 1
          : Math.max(0, newGuests[type] - 1);

      // Ensure at least 1 adult
      if (type === "adults" && newGuests.adults < 1) {
        newGuests.adults = 1;
      }

      return { ...prev, guests: newGuests };
    });
  };

  const updateRooms = (action) => {
    setFormData((prev) => ({
      ...prev,
      rooms:
        action === "increment" ? prev.rooms + 1 : Math.max(1, prev.rooms - 1),
    }));
  };

  const guestSummary = `${formData.rooms} ${
    formData.rooms > 1 ? "Rooms" : "Room"
  }, ${formData.guests.adults + formData.guests.children} Guests`;

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="w-full max-w-6xl mx-auto bg-black bg-opacity-20 backdrop-blur-sm p-4 md:p-6 rounded-lg">
        <div className="bg-white w-full flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2 p-3 md:p-5 rounded-3xl">
          <input
            type="text"
            name="location"
            placeholder="Where are you going?"
            className="flex-1 p-3 rounded-xl md:rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
            onChange={handleLocationChange}
            value={formData.location}
            list="location-suggestions"
          />
          <datalist id="location-suggestions">
            {locationSuggestions.map((suggestion, index) => (
              <option key={index} value={suggestion.description} />
            ))}
          </datalist>

          <DatePicker
            selected={formData.dateRange[0]}
            onChange={handleDateChange}
            startDate={formData.dateRange[0]}
            endDate={formData.dateRange[1]}
            selectsRange
            minDate={new Date()}
            className="flex-1 p-3 rounded-xl md:rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 w-full"
            placeholderText="Check-in - Check-out"
          />

          <button
            className="flex-1 p-3 rounded-xl md:rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 truncate overflow-hidden whitespace-nowrap text-left"
            onClick={() => setIsModalOpen(true)}
          >
            {guestSummary}
          </button>

          <button
            className={`h-12 w-full md:w-12 ${config.primaryColorBg} text-black rounded-xl md:rounded-full flex items-center justify-center hover:bg-purple-700 transition-colors`}
            onClick={handleSearch}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </button>
        </div>
      </div>

      {/* Guest & Rooms Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-md p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-left mb-6">
              Rooms & Guests
            </h3>

            {/* Rooms Selection */}
            <div className="flex justify-between mb-6">
              <p className="font-bold text-black">Rooms</p>
              <div className="flex items-center">
                <button
                  className="px-2 rounded-full border"
                  onClick={() => updateRooms("decrement")}
                >
                  -
                </button>
                <span className="px-4">{formData.rooms}</span>
                <button
                  className="px-2 rounded-full border"
                  onClick={() => updateRooms("increment")}
                >
                  +
                </button>
              </div>
            </div>

            {/* Adults Selection */}
            <div className="flex justify-between mb-6">
              <p className="font-light text-gray-500">
                <span className="font-bold text-black">Adults</span>
              </p>
              <div className="flex items-center">
                <button
                  className="px-2 rounded-full border"
                  onClick={() => updateGuests("adults", "decrement")}
                >
                  -
                </button>
                <span className="px-4">{formData.guests.adults}</span>
                <button
                  className="px-2 rounded-full border"
                  onClick={() => updateGuests("adults", "increment")}
                >
                  +
                </button>
              </div>
            </div>

            {/* Children Selection */}
            <div className="flex justify-between mb-6">
              <p className="font-light text-gray-500">
                <span className="font-bold text-black">Children</span>
              </p>
              <div className="flex items-center">
                <button
                  className="px-2 rounded-full border"
                  onClick={() => updateGuests("children", "decrement")}
                >
                  -
                </button>
                <span className="px-4">{formData.guests.children}</span>
                <button
                  className="px-2 rounded-full border"
                  onClick={() => updateGuests("children", "increment")}
                >
                  +
                </button>
              </div>
            </div>

            {formData.guests.children > 0 && (
              <div className="space-y-4">
                {Array.from({ length: formData.guests.children }).map(
                  (_, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center"
                    >
                      <p className="font-light text-gray-500">
                        <span className="font-bold text-black">
                          Child {index + 1} Age
                        </span>
                      </p>
                      <input
                        type="number"
                        min="0"
                        max="17"
                        value={formData.guests.childrenAges[index] || ""}
                        onChange={(e) => {
                          const newAges = [...formData.guests.childrenAges];
                          newAges[index] = parseInt(e.target.value);
                          setFormData((prev) => ({
                            ...prev,
                            guests: {
                              ...prev.guests,
                              childrenAges: newAges,
                            },
                          }));
                        }}
                        className="w-20 px-2 py-1 border rounded"
                        placeholder="Age"
                      />
                    </div>
                  )
                )}
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <button
                className={`mt-4 text-gray-700 ${config.primaryColorBg}  px-4 py-2 rounded`}
                onClick={() => setIsModalOpen(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchForm;
